import { IconName, CalendarItem } from '@wui/common-library'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { useStoreContacts } from '@/entities/contact'

import { CallBack } from '@/shared/types/api'

import { callBackAPI } from '../api'

export const useCallBack = defineStore('call-back', () => {
  const { findByInternalNumber } = useStoreContacts()

  const callBackLists = ref(new Map<string, CallBack>())
  const callBackNewTmp = ref<Omit<CallBack, 'guid'> | null>()
  const isLoading = ref(false)

  const loadAllCallBack = async () => {
    isLoading.value = false
    return await callBackAPI().loadAllCallBack().then((result: { data: Array<CallBack>; status: number }) => {
      const { data, status } = result
      if (status === 200) {
        data.forEach((items: CallBack) => {
          callBackLists.value.set(items.guid || items.attemptCount.toString(), items)
        })
      }
    }).finally(() => {
      isLoading.value = false
    })
  }

  const saveCallBack = async (saveItem: Omit<CallBack, 'guid'>) => {
    isLoading.value = true
    return await callBackAPI().saveCallBackService(saveItem).then((result: { status: number }) => {
      const { status } = result
      if (status === 201) {
        loadAllCallBack()
        callBackNewTmp.value = null
      }
    }).finally(() => {
      isLoading.value = false
    })
  }

  const deleteCallBack = async (guid: string) => {
    isLoading.value = true
    return await callBackAPI().deleteCallBack(guid).then((result: { status: number }) => {
      const { status } = result
      if (status) {
        callBackLists.value.delete(guid)
      }
    }).finally(() => {
      isLoading.value = false
    })
  }

  const updateNewCallBack = (callBackData: Omit<CallBack, 'guid'>) => {
    callBackNewTmp.value = callBackData
  }

  const getCallBackListFromArray = computed(() => {
    return Array.from(callBackLists.value.values())
  })

  const getActiveBackListFromArray = computed(() => {
    return Array.from(callBackLists.value.values()).find((item) => item.status === 0)
  })

  const isValidNewCallBack = computed(() => {
    return !!callBackNewTmp.value
  })

  const getCallBackConvertToCalendar = computed(() => {
    return Array.from(callBackLists.value.values()).map((item: CallBack) => {
      const getContact = findByInternalNumber(item.src || '')
      const nameContact = getContact ? getContact.name : ''
      return {
        id: item.guid,
        timestamp: dayjs(item.datetime).unix(),
        description: nameContact || item.src,
        type: 'primary',
        icon: 'callback' as IconName,
        eventType: 'callbacks',
      } as CalendarItem
    })
  })

  const findByGuid = (guid: CallBack['guid'] & string): CallBack | undefined => {
    return callBackLists.value.get(guid)
  }

  return {
    isLoading,
    callBackNewTmp,
    callBackLists,
    getCallBackListFromArray,
    getActiveBackListFromArray,
    isValidNewCallBack,
    getCallBackConvertToCalendar,
    findByGuid,
    loadAllCallBack,
    saveCallBack,
    deleteCallBack,
    updateNewCallBack,
  }
})
